'use client';

import { Switch as SwitchPrimitive } from '@base-ui/react/switch';
import { cn } from '@savvy-kit/lib/utils';

import { Label } from '../label/label';

type SwitchProps = SwitchPrimitive.Root.Props & {
  label?: string;
  description?: string;
  labelPosition?: 'left' | 'right';
  variant?: 'default' | 'box';
  id?: string;
};

function Switch({
  className,
  label,
  description,
  labelPosition = 'right',
  variant = 'default',
  id,
  ...props
}: SwitchProps) {
  const hasLabel = label ?? description;

  const track = (
    <SwitchPrimitive.Root
      data-slot="switch"
      id={id}
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-xs transition-colors outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-unchecked:bg-input data-checked:bg-primary',
        // Dark mode: --input is white at 10% alpha, which disappears on dark surfaces.
        // white/40 over zinc-950 is the lowest standard alpha step that clears WCAG 2.1
        // SC 1.4.11's 3:1 minimum for UI component boundaries (~3.8:1 contrast).
        'dark:data-unchecked:bg-white/40',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block size-4 rounded-full bg-white shadow-md ring-0 transition-transform',
          'data-unchecked:translate-x-0 data-checked:translate-x-4',
        )}
      />
    </SwitchPrimitive.Root>
  );

  const labelContent = hasLabel ? (
    <div className="flex flex-col gap-0.5">
      {label && (
        <Label htmlFor={id} className="text-sm font-semibold text-dark leading-5 cursor-pointer">
          {label}
        </Label>
      )}
      {description && <p className="text-sm text-secondary leading-snug">{description}</p>}
    </div>
  ) : null;

  if (!hasLabel) {
    return track;
  }

  const content = (
    <div className={cn('flex items-start gap-3', labelPosition === 'left' && 'flex-row-reverse justify-between')}>
      <div className="mt-[3px]">{track}</div>
      {labelContent}
    </div>
  );

  if (variant === 'box') {
    return (
      <div className="flex items-start rounded-lg border border-border-light bg-background p-4 shadow-xs">
        {content}
      </div>
    );
  }

  return content;
}

export { Switch };
export type { SwitchProps };
